import type {
  ChargeOperation,
  QuoteDetailPublic,
  QuoteDispositionKind,
  QuoteTaskChargePublic,
  QuoteTaskDetailPublic,
  QuoteTaskStatus,
  TaskOperation,
  UpdateCharge,
  UpdateQuote,
  UpdateTask,
} from "@common/client"
import { decimal, given, only } from "../optimistic"

const TASK_FIELDS: ReadonlySet<string> = new Set<keyof UpdateTask>([
  "operation",
  "id",
  "description",
  "severity",
  "status",
  "number",
  "manual_reference",
  "resolution",
  "ata_chapter",
  "ata_subchapter",
  "ata_zone",
  "canned_work_task_id",
  "task_group_id",
  "phase_ids",
  "charges",
])

const CHARGE_FIELDS: ReadonlySet<string> = new Set<keyof UpdateCharge>([
  "operation",
  "id",
  "description",
  "type",
  "part_number",
  "purchase_unit_price",
  "sale_unit_price",
  "currency",
  "quantity",
  "unit_of_measure",
  "tax_amount",
  "lead_time",
  "turnaround_time",
  "estimate_kind",
  "cost_quantity",
  "vendor_id",
])

function predictableCharge(operation: ChargeOperation): boolean {
  switch (operation.operation) {
    case "update":
      return only(CHARGE_FIELDS, operation)
    case "remove":
      return true
    case "create":
    case "set":
      return false
  }
}

function predictableTask(operation: TaskOperation): boolean {
  switch (operation.operation) {
    case "update":
      return (
        only(TASK_FIELDS, operation) &&
        (operation.charges ?? []).every(predictableCharge)
      )
    case "remove":
      return true
    case "create":
    case "create_from_template":
    case "apply_template":
      return false
  }
}

export function isPredictableUpdate(body: UpdateQuote): boolean {
  return (
    Object.keys(body).every((key) => key === "tasks") &&
    (body.tasks ?? []).every(predictableTask)
  )
}

function chargeWith(
  charge: QuoteTaskChargePublic,
  operation: UpdateCharge,
): QuoteTaskChargePublic {
  return {
    ...charge,
    description: given(operation.description, charge.description),
    type: given(operation.type, charge.type),
    part_number: given(operation.part_number, charge.part_number),
    purchase_unit_price: decimal(
      operation.purchase_unit_price,
      charge.purchase_unit_price,
    ),
    sale_unit_price: decimal(operation.sale_unit_price, charge.sale_unit_price),
    currency: given(operation.currency, charge.currency),
    quantity: decimal(operation.quantity, charge.quantity),
    unit_of_measure: given(operation.unit_of_measure, charge.unit_of_measure),
    tax_amount: decimal(operation.tax_amount, charge.tax_amount),
    lead_time: given(operation.lead_time, charge.lead_time),
    turnaround_time: given(operation.turnaround_time, charge.turnaround_time),
    estimate_kind: given(operation.estimate_kind, charge.estimate_kind),
    cost_quantity: decimal(operation.cost_quantity, charge.cost_quantity),
    vendor_id: given(operation.vendor_id, charge.vendor_id),
  }
}

function chargesWith(
  charges: QuoteTaskChargePublic[],
  operations: ChargeOperation[],
): QuoteTaskChargePublic[] {
  return operations.reduce((current, operation) => {
    switch (operation.operation) {
      case "update":
        return current.map((charge) =>
          charge.id === operation.id ? chargeWith(charge, operation) : charge,
        )
      case "remove":
        return current.filter((charge) => charge.id !== operation.id)
      case "create":
      case "set":
        return current
    }
  }, charges)
}

const UNTOUCHED_FIELDS: ReadonlySet<string> = new Set<keyof UpdateTask>([
  "operation",
  "id",
  "status",
  "task_group_id",
  "phase_ids",
  "charges",
  "document_references",
])

function touches(operation: UpdateTask): boolean {
  return (
    Object.entries(operation).some(
      ([key, value]) => value !== undefined && !UNTOUCHED_FIELDS.has(key),
    ) || (operation.charges ?? []).length > 0
  )
}

function touchedStatus(
  disposition: QuoteDispositionKind,
  status: QuoteTaskStatus,
): QuoteTaskStatus {
  if (status === "excluded") return status
  switch (disposition) {
    case "draft":
      return status === "clarification_requested" ? status : "draft"
    case "review":
      return status === "approved" ? "ready_for_customer" : status
    case "customer_response":
      return "ready_for_customer"
    case "customer_review":
    case "won":
    case "lost":
    case "cancelled":
    case "expired":
      return status
  }
}

function taskWith(
  task: QuoteTaskDetailPublic,
  operation: UpdateTask,
  disposition: QuoteDispositionKind,
): QuoteTaskDetailPublic {
  const status = given(
    operation.status,
    touches(operation) ? touchedStatus(disposition, task.status) : task.status,
  )
  return {
    ...task,
    description: given(operation.description, task.description) ?? "",
    severity: given(operation.severity, task.severity),
    status,
    awarded_kind: status === "approved" ? task.awarded_kind : null,
    number: given(operation.number, task.number),
    manual_reference: given(operation.manual_reference, task.manual_reference),
    resolution: given(operation.resolution, task.resolution),
    ata_chapter: given(operation.ata_chapter, task.ata_chapter),
    ata_subchapter: given(operation.ata_subchapter, task.ata_subchapter),
    ata_zone: given(operation.ata_zone, task.ata_zone),
    canned_work_task_id: given(
      operation.canned_work_task_id,
      task.canned_work_task_id,
    ),
    task_group_id: given(operation.task_group_id, task.task_group_id),
    phase_ids: given(operation.phase_ids, task.phase_ids),
    active_charges: chargesWith(task.active_charges, operation.charges ?? []),
  }
}

export function quoteWithUpdate(
  quote: QuoteDetailPublic,
  body: UpdateQuote,
): QuoteDetailPublic {
  return {
    ...quote,
    active_tasks: (body.tasks ?? []).reduce((tasks, operation) => {
      switch (operation.operation) {
        case "update":
          return tasks.map((task) =>
            task.id === operation.id
              ? taskWith(task, operation, quote.disposition)
              : task,
          )
        case "remove":
          return tasks.filter((task) => task.id !== operation.id)
        case "create":
        case "create_from_template":
        case "apply_template":
          return tasks
      }
    }, quote.active_tasks),
  }
}
